import React from 'react';
import { Box, Typography, Container, Link } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import VideoBanner from '../components/VideoBanner/VideoBanner';
import { useLanguage } from '../context/LanguageContext';

const ContactCard = ({ name, position, phone, email }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Box
      sx={{
        backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        p: 3,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
        },
        zIndex: 1000,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          color: '#dfdfdf',
          fontWeight: 600,
          mb: 1,
        }}
      >
        {name}
      </Typography>
      
      <Typography
        variant="subtitle1"
        sx={{
          color: '#dfdfdf',
          mb: 2,
          fontWeight: 500,
        }}
      >
        {position}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneIcon sx={{ color: '#dfdfdf' }} />
          <Link
            href={`tel:${phone}`}
            sx={{
              color: '#dfdfdf',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                color: '#dfdfdf',
                textShadow: '0 0 8px rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            {phone}
          </Link>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon sx={{ color: '#dfdfdf' }} />
          <Link
            href={`mailto:${email}`}
            sx={{
              color: '#dfdfdf',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                color: '#dfdfdf',
                textShadow: '0 0 8px rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            {email}
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

const Contacto = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { lang } = useLanguage();

  const copy = {
    es: {
      heading: 'Sobre nosotros',
      body:
        'Kuarzo Content nace de la unión de experiencias en el mercado audiovisual. Nuestros años de trabajo en agencia, productora y TV nos permiten ofrecer la mejor solución, al mejor costo y al nivel que nuestros clientes exigen.',
      quoteFirst:
        '"En Kuarzo Content tenemos el espíritu de dejar todo en la cancha, de tratar a todos los clientes por igual -grandes o pequeños- y de brindar un servicio de calidad. Sabemos que en toda producción surgen desafíos, pero lo importante es abordarlos con seriedad y dar una respuesta inmediata. ',
      quoteSecond: 'Esa es nuestra filosofía: cuidar cada detalle, no dejar nada librado al azar y mantener una actitud profesional ante cualquier situación. Así, el cliente se siente contenido. Es parte del ADN de Kuarzo Content y de los valores que compartimos con Marcelo y Pato: profesionalismo y respuesta por encima de todo."',
      quoteAuthor: '- Andy Kweller',
      cards: [
        { name: 'Andy Kweller', position: 'Managing Director' },
        { name: 'Marcelo Ramos', position: 'Productor Ejecutivo' },
        { name: 'Patricio Llados', position: 'Director de Producción' },
      ],
      address: 'Jose A. Cabrera 5870 CABA, Argentina',
    },
    en: {
      heading: 'About us',
      body:
        'Kuarzo Content emerged from the union of experience across the audiovisual industry. Our years working in agencies, production companies, and television allow us to offer the best solutions, at the best cost, and at the level our clients expect..',
      quoteFirst:
        '"At Kuarzo Content, we are committed to giving our all, treating every client equally -big or small- and delivering quality service. We know that every production comes with its challenges, but what matters is facing them seriously and providing an immediate response. ',
      quoteSecond: 'That is our philosophy: taking care of every detail, leaving nothing to chance, and maintaining a professional attitude in any situation. This is how clients feel supported. It’s part of Kuarzo Content’s DNA and of the values we share with Marcelo and Pato: professionalism and responsiveness above all else."',
        quoteAuthor: '- Andy Kweller',
      cards: [
        { name: 'Andy Kweller', position: 'Managing Director' },
        { name: 'Marcelo Ramos', position: 'Executive Producer' },
        { name: 'Patricio Llados', position: 'Production Director' },
      ],
      address: 'Jose A. Cabrera 5870, Buenos Aires, Argentina',
    },
  };

  const t = copy[lang] || copy.es;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: { xs: '80px', md: '10px' },
        pb: { xs: '50px', md: '55px' },
        display: 'flex',
        alignItems: 'flex-start',
        mt: isMobile ? 0 : 0,
        overflowX: 'hidden',
      }}
    >
      {!isMobile && <VideoBanner />}

      <Container 
        maxWidth="xl"
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: { xs: 'auto', md: '100%' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              zIndex: 1000,
              marginTop: isMobile ? '20px' : '6vw',
            }}
          >
            <Typography 
              variant="h4"
              sx={{
                color: '#FFFFFF',
                mb: 2,
                fontWeight: 600,
                letterSpacing: '1px',
              }}
            >
              {t.heading}
            </Typography>
            <Typography 
              variant="body1"
              sx={{
                fontSize: { xs: '1.05rem', md: '1.1rem' },
                lineHeight: 1.7,
                color: '#FFFFFF',
                opacity: 1,
                fontWeight: 400,
                mb: 1,
              }}
            >
              {t.body}
            </Typography>
            <Box
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                p: { xs: 2.5, md: 3 },
                color: '#FFFFFF',
              }}
            >
              <Typography 
                variant="body1"
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.1rem' },
                  lineHeight: 1.7,
                  color: '#FFFFFF',
                  opacity: 1,
                  fontWeight: 400,
                }}
              >
                {t.quoteFirst}
                <br />
                {t.quoteSecond}
                <br />
                {t.quoteAuthor}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            <ContactCard
              name="Andy Kweller"
              position={t.cards[0].position}
              phone="+54 9 11 3287 4265"
              email="akweller@kuarzocontent.com"
            />
            
            <ContactCard
              name="Marcelo Ramos"
              position={t.cards[1].position}
              phone="+54 9 11 5569 6814"
              email="mramos@kuarzocontent.com"
            />
            <ContactCard
              name="Patricio Llados"
              position={t.cards[2].position}
              phone="+54 9 11 5342 7197"
              email="pllados@kuarzocontent.com"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 2,
            }}
          >
            <LocationOnIcon sx={{ color: '#FFFFFF' }} />
            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: { xs: '1rem', md: '1.05rem' },
              }}
            >
              Jose A. Cabrera 5870 CABA, Argentina
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Contacto;
